<template>
  <div v-if="dialogVisible" class="nr-dialog-overlay" @click.self="handleClose">
    <div class="nr-dialog" :class="{ fullscreen: isMobile }" role="dialog" aria-modal="true" aria-labelledby="book-editor-title">
      <div class="nr-dialog__header">
        <div><span class="nr-kicker">BOOK INFORMATION</span><h3 id="book-editor-title">编辑书籍信息</h3></div>
        <button class="close-btn" aria-label="关闭" @click="handleClose"><nricon name="close" /></button>
      </div>

      <div class="nr-dialog__body">
        <div class="form-cols">
          <div class="col-main">
            <div class="nr-field">
              <label class="nr-field__label">书名</label>
              <input v-model="bookForm.title" class="nr-input" placeholder="请输入书名">
            </div>
            <div class="nr-field">
              <label class="nr-field__label">作者</label>
              <input v-model="bookForm.author" class="nr-input" placeholder="请输入作者">
            </div>
            <div class="nr-field">
              <label class="nr-field__label">可见性</label>
              <div class="nr-radios">
                <button type="button" class="nr-radio" :class="{ active: bookForm.visibility === 'PRIVATE' }" @click="bookForm.visibility = 'PRIVATE'"><nricon name="lock" /> 私有</button>
                <button type="button" class="nr-radio" :class="{ active: bookForm.visibility === 'PUBLIC' }" @click="bookForm.visibility = 'PUBLIC'"><nricon name="view" /> 公开</button>
              </div>
            </div>
            <div class="nr-field">
              <label class="nr-field__label">分类</label>
              <select v-model="bookForm.category" class="nr-select">
                <option value="">未分类</option>
                <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div class="nr-field">
              <label class="nr-field__label">标签</label>
              <input v-model="bookForm.tags" class="nr-input" placeholder="多个标签用英文逗号分隔，如：人妻,熟女,NTR">
            </div>
          </div>

          <div class="col-cover">
            <label class="nr-field__label">封面</label>
            <button type="button" class="cover-uploader" aria-label="上传或更换封面" @click="triggerCoverUpload">
              <protected-cover v-if="bookForm.coverImage" :src="bookForm.coverImage" class="cover-preview" alt="封面预览" fit="contain" />
              <div v-else class="cover-placeholder"><nricon name="plus" /><span>点击上传</span></div>
            </button>
            <input type="file" accept="image/*" @change="handleCoverChange" ref="coverInput" style="display: none">
            <button class="btn btn--sm btn--block" @click="triggerCoverUpload">{{ bookForm.coverImage ? '更换封面' : '上传封面' }}</button>
            <p class="cover-hint">建议 &lt; 1MB</p>
          </div>
        </div>

        <div class="nr-field">
          <label class="nr-field__label">简介</label>
          <textarea v-model="bookForm.description" class="nr-textarea" rows="4" placeholder="请输入书籍简介"></textarea>
        </div>
      </div>

      <div class="nr-dialog__footer">
        <button class="btn" @click="handleClose">取消</button>
        <button class="btn btn--primary" :disabled="saving" @click="handleSubmit">
          <nricon v-if="saving" name="loading" />
          <span v-else>保存修改</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { CATEGORIES } from '@/utils/categories'
import ProtectedCover from '@/components/common/ProtectedCover.vue'

export default {
  name: 'BookEditor',
  components: { ProtectedCover },
  props: {
    book: {
      type: Object,
      required: true,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      dialogVisible: false,
      bookForm: {
        title: '',
        author: '',
        description: '',
        coverImage: '',
        visibility: 'PRIVATE',
        category: '',
        tags: ''
      },
      saving: false,
      isMobile: window.innerWidth <= 768,
      categories: CATEGORIES
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val
        if (val) {
          this.initForm()
        }
      }
    },
    dialogVisible(val) {
      if (val !== this.visible) {
        this.$emit('update:visible', val)
      }
      this.lockScroll(val)
    },
    book: {
      handler(val) {
        if (val) {
          this.initForm()
        }
      },
      immediate: true
    }
  },
  created() {
    window.addEventListener('resize', this.checkMobile)
    document.addEventListener('keydown', this.onKeydown)
  },
  mounted() {
    this.lockScroll(this.dialogVisible)
  },
  destroyed() {
    window.removeEventListener('resize', this.checkMobile)
    document.removeEventListener('keydown', this.onKeydown)
    this.lockScroll(false)
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.dialogVisible) {
        this.handleClose()
      }
    },
    lockScroll(lock) {
      document.body.style.overflow = lock ? 'hidden' : ''
    },
    initForm() {
      this.bookForm = {
        title: this.book.title || '',
        author: this.book.author || '',
        description: this.book.description || '',
        coverImage: this.book.coverImage || '',
        visibility: this.book.visibility || 'PRIVATE',
        category: this.book.category || '',
        tags: this.book.tags || ''
      }
    },
    triggerCoverUpload() {
      this.$refs.coverInput.click()
    },
    async handleCoverChange(e) {
      const file = e.target.files[0]
      if (!file) return

      if (file.size > 1024 * 1024) {
        this.$message.error('图片大小不能超过1MB')
        return
      }

      try {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          this.bookForm.coverImage = reader.result
        }
      } catch (error) {
        this.$message.error('图片处理失败')
      }
    },
    async handleSubmit() {
      this.saving = true
      try {
        const response = await this.$axios.put(
          `/books/${this.book.id}`,
          this.bookForm
        )
        this.$message.success('保存成功')
        this.$emit('success', response.data)
        this.handleClose()
      } catch (error) {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    handleClose() {
      this.dialogVisible = false
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.nr-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.nr-dialog {
  width: 100%;
  max-width: 520px;
  background: var(--nr-surface);
  border-radius: var(--nr-radius-lg);
  box-shadow: var(--nr-shadow-hover);
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  &.fullscreen {
    max-width: none;
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--nr-border);
    h3 { margin: 0; font-size: 16px; font-weight: 500; color: var(--nr-text); }
    .close-btn {
      border: none; background: transparent; cursor: pointer;
      font-size: 20px; color: var(--nr-text-3); padding: 2px;
      &:hover { color: var(--nr-text); }
    }
  }

  &__body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid var(--nr-border);
  }
}

.form-cols {
  display: flex;
  gap: 20px;

  .col-main { flex: 1; min-width: 0; }
  .col-cover { width: 128px; flex-shrink: 0; }
}

.cover-uploader {
  width: 100%;
  height: 168px;
  border: 1px dashed var(--nr-border);
  border-radius: var(--nr-radius);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  margin-bottom: 8px;
  &:hover { border-color: var(--nr-accent); }
}

.cover-preview { width: 100%; height: 100%; object-fit: cover; }

.cover-placeholder {
  width: 100%; height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  color: var(--nr-text-3);
  background: var(--nr-surface-2);
  i { font-size: 26px; }
  span { font-size: 12px; }
}

.cover-hint { margin: 6px 0 0; font-size: 11px; color: var(--nr-text-3); text-align: center; }

@media screen and (max-width: 768px) {
  .form-cols { flex-direction: column; }
  .form-cols .col-cover { width: 100%; }
  .cover-uploader { height: 200px; }
}
</style>

<style lang="scss" scoped>
.nr-dialog-overlay {
  padding: 24px;
  background: rgba(18,16,13,.62);
  backdrop-filter: blur(9px);
}

.nr-dialog {
  max-width: 620px;
  overflow: hidden;
  border: 1px solid var(--nr-border);
  border-radius: var(--nr-radius-xl);
  box-shadow: var(--nr-shadow-float);
}

.nr-dialog__header {
  padding: 24px 28px 20px;
  h3 { margin-top: 5px; font-family: var(--nr-font-display); font-size: 26px; font-weight: 600; letter-spacing: -.025em; }
  .close-btn { display: grid; width: 44px; height: 44px; place-items: center; padding: 0; border-radius: 50%; transition: color 180ms ease, background 180ms ease; }
  .close-btn:hover { color: var(--nr-text); background: var(--nr-surface-2); }
}

.nr-dialog__body { padding: 26px 28px 16px; }
.nr-dialog__footer { padding: 18px 28px 24px; border-top: 0; }
.nr-dialog__footer .btn--primary { min-width: 118px; }

.form-cols { gap: 28px; }
.form-cols .col-cover { width: 145px; }

.cover-uploader {
  display: block;
  height: 194px;
  padding: 0;
  color: inherit;
  border-color: var(--nr-border-strong);
  border-radius: 7px 12px 12px 7px;
  background: var(--nr-surface-2);
  box-shadow: 6px 10px 20px rgba(42,32,22,.12);
  transition: border-color 180ms ease, transform 180ms ease;

  &:hover { border-color: var(--nr-accent); transform: translateY(-2px); }
}

.cover-placeholder { background: linear-gradient(145deg, var(--nr-surface-2), var(--nr-bg-deep)); }
.cover-hint { margin-top: 8px; }
.nr-textarea { min-height: 112px; }

@media (max-width: 768px) {
  .nr-dialog-overlay { padding: 0; }
  .nr-dialog.fullscreen { height: 100dvh; background: var(--nr-surface); }
  .nr-dialog__header { padding: max(18px, env(safe-area-inset-top)) 18px 16px; }
  .nr-dialog__body { padding: 22px 18px; }
  .nr-dialog__footer { padding: 14px 18px max(18px, env(safe-area-inset-bottom)); }
  .form-cols { gap: 18px; }
  .form-cols .col-cover { width: 145px; }
  .cover-uploader { height: 194px; }
}
</style>
